import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import type { SummaryContextSnapshot, TweetSummaryDataActions, TweetSummaryDataState, TweetSummaryMetadata, TweetSummarySettingsState, UseTweetSummaryParameters } from '../types';
import { buildSummaryPromptText } from '../utils';
import { useSummaryChat } from './useSummaryChat';
import { useSummarySettings } from './useSummarySettings';
import { useSummaryStreams } from './useSummaryStreams';
type TweetSummaryDataResult = {
  state: TweetSummaryDataState;
  actions: TweetSummaryDataActions;
  settings: TweetSummarySettingsState;
  metadata: TweetSummaryMetadata;
};
export function useTweetSummaryData(parameters) {
  const $ = _c(80);
  const settingsResult = useSummarySettings();
  const {
    base: baseSettings,
    metadata: settingsMetadata,
    actions: settingsActions
  } = settingsResult;
  const summaryImagesAvailable = parameters.summaryImages.length > 0;
  const includeImagesInPrompt = summaryImagesAvailable;
  let t0;
  if ($[0] !== parameters.summaryImages || $[1] !== parameters.tweet) {
    t0 = (custom, includeImages) => buildSummaryPromptText({
      tweet: parameters.tweet,
      summaryImages: parameters.summaryImages,
      includeImages,
      customPrompt: custom
    });
    $[0] = parameters.summaryImages;
    $[1] = parameters.tweet;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const buildPrompt = t0;
  const [summaryContextSnapshot, setSummaryContextSnapshot] = useState(null);
  let t1;
  if ($[3] !== baseSettings || $[4] !== buildPrompt || $[5] !== includeImagesInPrompt || $[6] !== parameters.appendMessages || $[7] !== parameters.inlineTweetUrls || $[8] !== parameters.isChatEnabled || $[9] !== parameters.longFormHint || $[10] !== parameters.openChat || $[11] !== parameters.registerContext || $[12] !== parameters.summaryImages || $[13] !== parameters.tweet || $[14] !== settingsActions) {
    t1 = {
      tweet: parameters.tweet,
      longFormHint: parameters.longFormHint,
      summaryImages: parameters.summaryImages,
      inlineTweetUrls: parameters.inlineTweetUrls,
      includeImagesInPrompt,
      isChatEnabled: parameters.isChatEnabled,
      openChat: parameters.openChat,
      registerContext: parameters.registerContext,
      appendMessages: parameters.appendMessages,
      settings: baseSettings,
      actions: settingsActions,
      buildPrompt,
      setSummaryContextSnapshot
    };
    $[3] = baseSettings;
    $[4] = buildPrompt;
    $[5] = includeImagesInPrompt;
    $[6] = parameters.appendMessages;
    $[7] = parameters.inlineTweetUrls;
    $[8] = parameters.isChatEnabled;
    $[9] = parameters.longFormHint;
    $[10] = parameters.openChat;
    $[11] = parameters.registerContext;
    $[12] = parameters.summaryImages;
    $[13] = parameters.tweet;
    $[14] = settingsActions;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  const streams = useSummaryStreams(t1);
  let t2;
  if ($[16] !== baseSettings.customPrompt || $[17] !== baseSettings.summaryLengthIndex || $[18] !== baseSettings.summaryModel || $[19] !== baseSettings.summarySearchEnabled || $[20] !== buildPrompt || $[21] !== includeImagesInPrompt || $[22] !== parameters.appendMessages || $[23] !== parameters.isChatEnabled || $[24] !== parameters.metrics || $[25] !== parameters.openChat || $[26] !== parameters.registerContext || $[27] !== parameters.tweet || $[28] !== streams.summaryState || $[29] !== summaryContextSnapshot) {
    t2 = {
      tweet: parameters.tweet,
      metrics: parameters.metrics,
      summaryState: streams.summaryState,
      isChatEnabled: parameters.isChatEnabled,
      includeImagesInPrompt,
      customPrompt: baseSettings.customPrompt,
      summaryLengthIndex: baseSettings.summaryLengthIndex,
      summaryModel: baseSettings.summaryModel,
      summarySearchEnabled: baseSettings.summarySearchEnabled,
      openChat: parameters.openChat,
      appendMessages: parameters.appendMessages,
      registerContext: parameters.registerContext,
      buildPrompt,
      summaryContextSnapshot,
      setSummaryContextSnapshot
    };
    $[16] = baseSettings.customPrompt;
    $[17] = baseSettings.summaryLengthIndex;
    $[18] = baseSettings.summaryModel;
    $[19] = baseSettings.summarySearchEnabled;
    $[20] = buildPrompt;
    $[21] = includeImagesInPrompt;
    $[22] = parameters.appendMessages;
    $[23] = parameters.isChatEnabled;
    $[24] = parameters.metrics;
    $[25] = parameters.openChat;
    $[26] = parameters.registerContext;
    $[27] = parameters.tweet;
    $[28] = streams.summaryState;
    $[29] = summaryContextSnapshot;
    $[30] = t2;
  } else {
    t2 = $[30];
  }
  const {
    chatFromSummary
  } = useSummaryChat(t2);
  let t3;
  if ($[31] !== includeImagesInPrompt || $[32] !== streams.canExplain || $[33] !== streams.canSummarize || $[34] !== streams.explanationState || $[35] !== streams.hasSummary || $[36] !== streams.isExplaining || $[37] !== streams.isSummarizing || $[38] !== streams.shouldRenderSummaryControls || $[39] !== streams.showMoreVisible || $[40] !== streams.summarizeButtonLabel || $[41] !== streams.summaryState) {
    t3 = {
      includeImagesInPrompt,
      summaryState: streams.summaryState,
      explanationState: streams.explanationState,
      isSummarizing: streams.isSummarizing,
      isExplaining: streams.isExplaining,
      canSummarize: streams.canSummarize,
      canExplain: streams.canExplain,
      shouldRenderSummaryControls: streams.shouldRenderSummaryControls,
      summarizeButtonLabel: streams.summarizeButtonLabel,
      showMoreVisible: streams.showMoreVisible,
      hasSummary: streams.hasSummary
    };
    $[31] = includeImagesInPrompt;
    $[32] = streams.canExplain;
    $[33] = streams.canSummarize;
    $[34] = streams.explanationState;
    $[35] = streams.hasSummary;
    $[36] = streams.isExplaining;
    $[37] = streams.isSummarizing;
    $[38] = streams.shouldRenderSummaryControls;
    $[39] = streams.showMoreVisible;
    $[40] = streams.summarizeButtonLabel;
    $[41] = streams.summaryState;
    $[42] = t3;
  } else {
    t3 = $[42];
  }
  let t4;
  if ($[43] !== chatFromSummary || $[44] !== settingsActions.closeSettings || $[45] !== settingsActions.openSettings || $[46] !== streams.explain || $[47] !== streams.handleShowMoreVisibility || $[48] !== streams.runActiveMode || $[49] !== streams.submitSettings || $[50] !== streams.summarize) {
    t4 = {
      summarize: streams.summarize,
      explain: streams.explain,
      chatFromSummary,
      openSettings: settingsActions.openSettings,
      closeSettings: settingsActions.closeSettings,
      submitSettings: streams.submitSettings,
      runActiveMode: streams.runActiveMode,
      handleShowMoreVisibility: streams.handleShowMoreVisibility
    };
    $[43] = chatFromSummary;
    $[44] = settingsActions.closeSettings;
    $[45] = settingsActions.openSettings;
    $[46] = streams.explain;
    $[47] = streams.handleShowMoreVisibility;
    $[48] = streams.runActiveMode;
    $[49] = streams.submitSettings;
    $[50] = streams.summarize;
    $[51] = t4;
  } else {
    t4 = $[51];
  }
  let t5;
  if ($[52] !== baseSettings.summaryLengthIndex || $[53] !== baseSettings.summaryMode || $[54] !== baseSettings.summaryModel || $[55] !== baseSettings.summarySearchEnabled || $[56] !== settingsActions.setDraftCustomPrompt || $[57] !== settingsActions.setDraftModel || $[58] !== settingsActions.setDraftSummaryLengthIndex || $[59] !== settingsActions.setDraftSummaryMode || $[60] !== settingsActions.setDraftSummarySearchEnabled || $[61] !== settingsActions.setSummaryMode || $[62] !== settingsActions.setSummarySearchEnabled || $[63] !== settingsMetadata.canonicalDraftModel || $[64] !== settingsMetadata.settingsOpen || $[65] !== settingsResult.drafts.customPrompt || $[66] !== settingsResult.drafts.summaryLengthIndex || $[67] !== settingsResult.drafts.summaryMode || $[68] !== settingsResult.drafts.summaryModel || $[69] !== settingsResult.drafts.summarySearchEnabled) {
    t5 = {
      settingsOpen: settingsMetadata.settingsOpen,
      draftSummaryLengthIndex: settingsResult.drafts.summaryLengthIndex,
      setDraftSummaryLengthIndex: settingsActions.setDraftSummaryLengthIndex,
      draftModel: settingsResult.drafts.summaryModel,
      setDraftModel: settingsActions.setDraftModel,
      canonicalDraftModel: settingsMetadata.canonicalDraftModel,
      draftCustomPrompt: settingsResult.drafts.customPrompt,
      setDraftCustomPrompt: settingsActions.setDraftCustomPrompt,
      draftSummarySearchEnabled: settingsResult.drafts.summarySearchEnabled,
      setDraftSummarySearchEnabled: settingsActions.setDraftSummarySearchEnabled,
      summaryLengthIndex: baseSettings.summaryLengthIndex,
      summaryModel: baseSettings.summaryModel,
      summarySearchEnabled: baseSettings.summarySearchEnabled,
      setSummarySearchEnabled: settingsActions.setSummarySearchEnabled,
      summaryMode: baseSettings.summaryMode,
      setSummaryMode: settingsActions.setSummaryMode,
      draftSummaryMode: settingsResult.drafts.summaryMode,
      setDraftSummaryMode: settingsActions.setDraftSummaryMode
    };
    $[52] = baseSettings.summaryLengthIndex;
    $[53] = baseSettings.summaryMode;
    $[54] = baseSettings.summaryModel;
    $[55] = baseSettings.summarySearchEnabled;
    $[56] = settingsActions.setDraftCustomPrompt;
    $[57] = settingsActions.setDraftModel;
    $[58] = settingsActions.setDraftSummaryLengthIndex;
    $[59] = settingsActions.setDraftSummaryMode;
    $[60] = settingsActions.setDraftSummarySearchEnabled;
    $[61] = settingsActions.setSummaryMode;
    $[62] = settingsActions.setSummarySearchEnabled;
    $[63] = settingsMetadata.canonicalDraftModel;
    $[64] = settingsMetadata.settingsOpen;
    $[65] = settingsResult.drafts.customPrompt;
    $[66] = settingsResult.drafts.summaryLengthIndex;
    $[67] = settingsResult.drafts.summaryMode;
    $[68] = settingsResult.drafts.summaryModel;
    $[69] = settingsResult.drafts.summarySearchEnabled;
    $[70] = t5;
  } else {
    t5 = $[70];
  }
  let t6;
  if ($[71] !== settingsMetadata.liveSearchProviderName || $[72] !== settingsMetadata.providerHandlesVideo || $[73] !== settingsMetadata.supportsSummarySearch) {
    t6 = {
      supportsSummarySearch: settingsMetadata.supportsSummarySearch,
      liveSearchProviderName: settingsMetadata.liveSearchProviderName,
      providerHandlesVideo: settingsMetadata.providerHandlesVideo
    };
    $[71] = settingsMetadata.liveSearchProviderName;
    $[72] = settingsMetadata.providerHandlesVideo;
    $[73] = settingsMetadata.supportsSummarySearch;
    $[74] = t6;
  } else {
    t6 = $[74];
  }
  let t7;
  if ($[75] !== t3 || $[76] !== t4 || $[77] !== t5 || $[78] !== t6) {
    t7 = {
      state: t3,
      actions: t4,
      settings: t5,
      metadata: t6
    };
    $[75] = t3;
    $[76] = t4;
    $[77] = t5;
    $[78] = t6;
    $[79] = t7;
  } else {
    t7 = $[79];
  }
  return t7;
}